import type { LanguageModelV1, LanguageModelV1CallOptions, LanguageModelV1StreamPart } from 'ai';
import { BaseProvider } from '~/lib/modules/llm/base-provider';
import type { ModelInfo } from '~/lib/modules/llm/types';
import type { IProviderSetting } from '~/types/model';
import { createScopedLogger } from '~/utils/logger';

type ClaudeOutputFormat = 'json' | 'stream-json';
type ClaudeWarning = { type: 'other'; message: string };

const DEFAULT_CLAUDE_COMMAND = 'claude';
const DEFAULT_CLAUDE_MODEL = 'sonnet';
const DEFAULT_CLAUDE_PROMPT =
  'Follow the system instructions in the prompt. Native Claude Code tools are intentionally unavailable. ' +
  'For project changes, emit the requested devonzArtifact/devonzAction markup so the platform executes it. ' +
  'Never claim that you cannot create files, never ask the user to run commands, and return only the answer:';
const logger = createScopedLogger('ClaudeCLI');
const DEFAULT_DISALLOWED_TOOLS = [
  'Bash',
  'Edit',
  'Glob',
  'Grep',
  'LS',
  'MultiEdit',
  'NotebookEdit',
  'Read',
  'WebFetch',
  'WebSearch',
  'Write',
];

function splitShellLike(value: string | undefined): string[] {
  if (!value?.trim()) {
    return [];
  }

  const matches = value.match(/"([^"]*)"|'([^']*)'|[^\s]+/g) || [];

  return matches.map((part) => part.replace(/^(['"])(.*)\1$/, '$2'));
}

function messageContentToText(content: LanguageModelV1CallOptions['prompt'][number]['content']): string {
  if (typeof content === 'string') {
    return content;
  }

  return content
    .map((part) => {
      if (part.type === 'text') {
        return part.text;
      }

      if (part.type === 'tool-call') {
        return `[tool-call:${part.toolName}] ${JSON.stringify(part.args)}`;
      }

      if (part.type === 'tool-result') {
        return `[tool-result:${part.toolName}] ${JSON.stringify(part.result)}`;
      }

      if (part.type === 'image') {
        return '[image omitted: Claude CLI provider currently supports text-only prompts]';
      }

      if (part.type === 'file') {
        return `[file omitted: ${part.mimeType}]`;
      }

      return '';
    })
    .filter(Boolean)
    .join('\n');
}

function promptToText(options: LanguageModelV1CallOptions): string {
  const sections = options.prompt.map((message) => {
    const role = message.role.toUpperCase();
    const content = messageContentToText(message.content);

    return `<${role}>\n${content}\n</${role}>`;
  });

  return sections.join('\n\n');
}

function extractUsage(value: unknown): { promptTokens: number; completionTokens: number } | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const usage = (value as { usage?: Record<string, unknown> }).usage;

  if (!usage) {
    return undefined;
  }

  const promptTokens = Number(usage.input_tokens ?? usage.prompt_tokens ?? 0);
  const completionTokens = Number(usage.output_tokens ?? usage.completion_tokens ?? 0);

  return {
    promptTokens: Number.isFinite(promptTokens) ? promptTokens : 0,
    completionTokens: Number.isFinite(completionTokens) ? completionTokens : 0,
  };
}

function estimateTokens(text: string): number {
  return Math.max(1, Math.ceil(text.length / 4));
}

function parseClaudeJsonResult(stdout: string): {
  text: string;
  usage?: { promptTokens: number; completionTokens: number };
} {
  const trimmed = stdout.trim();

  if (!trimmed) {
    return { text: '' };
  }

  try {
    const parsed = JSON.parse(trimmed) as { result?: unknown; structured_output?: unknown };
    const result = parsed.result ?? parsed.structured_output;

    return {
      text: typeof result === 'string' ? result : JSON.stringify(result ?? parsed),
      usage: extractUsage(parsed),
    };
  } catch {
    return { text: trimmed };
  }
}

class ClaudeCliLanguageModel implements LanguageModelV1 {
  readonly specificationVersion = 'v1' as const;
  readonly provider = 'ClaudeCLI';
  readonly defaultObjectGenerationMode = undefined;
  readonly supportsImageUrls = false;
  readonly supportsStructuredOutputs = false;

  constructor(
    readonly modelId: string,
    private readonly _command: string,
    private readonly _extraArgs: string[] = [],
    private readonly _defaultModel: string = DEFAULT_CLAUDE_MODEL,
  ) {}

  async doGenerate(options: LanguageModelV1CallOptions) {
    const prompt = promptToText(options);
    const warnings = this._getWarnings(options);
    const { stdout } = await this._runClaude({
      format: 'json',
      prompt,
      signal: options.abortSignal,
    });
    const parsed = parseClaudeJsonResult(stdout);
    const usage = parsed.usage ?? {
      promptTokens: estimateTokens(prompt),
      completionTokens: estimateTokens(parsed.text),
    };

    return {
      text: parsed.text,
      finishReason: 'stop' as const,
      usage,
      rawCall: {
        rawPrompt: prompt,
        rawSettings: this._rawSettings('json'),
      },
      response: {
        timestamp: new Date(),
        modelId: this.modelId,
      },
      warnings,
    };
  }

  async doStream(options: LanguageModelV1CallOptions) {
    const prompt = promptToText(options);
    const warnings = this._getWarnings(options);
    const rawSettings = this._rawSettings('stream-json');

    return {
      stream: this._createClaudeStream(prompt, options.abortSignal),
      rawCall: {
        rawPrompt: prompt,
        rawSettings,
      },
      warnings,
    };
  }

  private _getWarnings(options: LanguageModelV1CallOptions): ClaudeWarning[] {
    const warnings: ClaudeWarning[] = [];

    if (options.mode.type === 'regular' && options.mode.tools?.length) {
      warnings.push({
        type: 'other',
        message: 'Claude CLI provider runs in text-only mode; AI SDK tool calls are not supported.',
      });
    }

    if (options.responseFormat?.type === 'json' || options.mode.type !== 'regular') {
      warnings.push({
        type: 'other',
        message: 'Claude CLI provider does not guarantee structured output through the AI SDK adapter.',
      });
    }

    return warnings;
  }

  private _rawSettings(format: ClaudeOutputFormat): Record<string, unknown> {
    return {
      command: this._command,
      format,
      model: this._resolveModel(),
      extraArgs: this._extraArgs,
    };
  }

  private _resolveModel(): string {
    return this.modelId === 'claude-cli-default' ? this._defaultModel : this.modelId.replace(/^claude-cli-/, '');
  }

  private _buildArgs(format: ClaudeOutputFormat): string[] {
    const args = ['-p', '--output-format', format];

    if (format === 'stream-json') {
      args.push('--verbose', '--include-partial-messages');
    }

    args.push('--model', this._resolveModel());

    const disallowedTools = process.env.CLAUDE_CLI_DISALLOWED_TOOLS?.trim()
      ? splitShellLike(process.env.CLAUDE_CLI_DISALLOWED_TOOLS)
      : DEFAULT_DISALLOWED_TOOLS;

    if (disallowedTools.length > 0) {
      args.push('--disallowedTools', ...disallowedTools);
    }

    args.push(...this._extraArgs);

    return args;
  }

  private async _runClaude(options: {
    format: ClaudeOutputFormat;
    prompt: string;
    signal?: AbortSignal;
  }): Promise<{ stdout: string; stderr: string }> {
    const { spawn } = await import('node:child_process');

    return await new Promise((resolve, reject) => {
      const child = spawn(this._command, this._buildArgs(options.format), {
        cwd: process.cwd(),
        env: process.env,
        shell: process.platform === 'win32',
        stdio: ['pipe', 'pipe', 'pipe'],
      });

      let stdout = '';
      let stderr = '';

      const abort = () => {
        child.kill();
        reject(new Error('Claude CLI call aborted'));
      };

      options.signal?.addEventListener('abort', abort, { once: true });

      child.stdin.end(`${DEFAULT_CLAUDE_PROMPT}\n\n${options.prompt}`);
      child.stdout.setEncoding('utf8');
      child.stderr.setEncoding('utf8');
      child.stdout.on('data', (chunk) => {
        stdout += chunk;
      });
      child.stderr.on('data', (chunk) => {
        stderr += chunk;
      });
      child.on('error', (error) => {
        options.signal?.removeEventListener('abort', abort);
        reject(error);
      });
      child.on('close', (code) => {
        options.signal?.removeEventListener('abort', abort);

        if (code === 0) {
          resolve({ stdout, stderr });
        } else {
          reject(new Error(`Claude CLI exited with code ${code}: ${stderr || stdout || 'no output'}`));
        }
      });
    });
  }

  private _createClaudeStream(prompt: string, signal?: AbortSignal): ReadableStream<LanguageModelV1StreamPart> {
    return new ReadableStream<LanguageModelV1StreamPart>({
      start: async (controller) => {
        const { spawn } = await import('node:child_process');
        const startedAt = performance.now();
        const input = `${DEFAULT_CLAUDE_PROMPT}\n\n${prompt}`;
        const inputBytes = new TextEncoder().encode(input).byteLength;
        const model = this._resolveModel();
        let firstEventAt: number | undefined;
        let firstTextAt: number | undefined;

        logger.info('Starting stream', {
          model,
          inputBytes,
          estimatedPromptTokens: estimateTokens(prompt),
        });

        const child = spawn(this._command, this._buildArgs('stream-json'), {
          cwd: process.cwd(),
          env: process.env,
          shell: process.platform === 'win32',
          stdio: ['pipe', 'pipe', 'pipe'],
        });

        const promptTokens = estimateTokens(prompt);
        let completionText = '';
        let stderr = '';
        let buffered = '';
        let finalUsage: { promptTokens: number; completionTokens: number } | undefined;

        const emitError = (error: unknown) => {
          controller.enqueue({ type: 'error', error });
        };

        const abort = () => {
          child.kill();
          emitError(new Error('Claude CLI call aborted'));
        };

        signal?.addEventListener('abort', abort, { once: true });

        child.stdin.end(input);
        child.stdout.setEncoding('utf8');
        child.stderr.setEncoding('utf8');
        child.stderr.on('data', (chunk) => {
          stderr += chunk;
        });
        child.stdout.on('data', (chunk: string) => {
          buffered += chunk;
          const lines = buffered.split(/\r?\n/);
          buffered = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.trim()) {
              continue;
            }

            try {
              const event = JSON.parse(line) as {
                type?: string;
                result?: string;
                event?: {
                  type?: string;
                  delta?: { type?: string; text?: string };
                  message?: { usage?: Record<string, unknown> };
                };
                usage?: Record<string, unknown>;
              };

              if (firstEventAt === undefined) {
                firstEventAt = performance.now();
                logger.info('First CLI event received', {
                  model,
                  elapsedMs: Math.round(firstEventAt - startedAt),
                });
              }

              const delta =
                event.type === 'stream_event' && event.event?.delta?.type === 'text_delta'
                  ? event.event.delta.text
                  : undefined;

              if (delta) {
                if (firstTextAt === undefined) {
                  firstTextAt = performance.now();
                  logger.info('First text delta received', {
                    model,
                    elapsedMs: Math.round(firstTextAt - startedAt),
                  });
                }

                completionText += delta;
                controller.enqueue({ type: 'text-delta', textDelta: delta });
              }

              const usage = extractUsage(event) || extractUsage(event.event?.message);

              if (usage) {
                finalUsage = usage;
              }
            } catch {
              // Claude CLI may emit non-JSON diagnostics on stderr/stdout in some versions.
            }
          }
        });
        child.on('error', emitError);
        child.on('close', (code) => {
          signal?.removeEventListener('abort', abort);
          logger.info('Stream completed', {
            model,
            exitCode: code,
            elapsedMs: Math.round(performance.now() - startedAt),
            inputBytes,
            outputCharacters: completionText.length,
            firstEventMs: firstEventAt === undefined ? null : Math.round(firstEventAt - startedAt),
            firstTextMs: firstTextAt === undefined ? null : Math.round(firstTextAt - startedAt),
          });

          if (code !== 0) {
            emitError(new Error(`Claude CLI exited with code ${code}: ${stderr || 'no output'}`));
          }

          controller.enqueue({
            type: 'finish',
            finishReason: code === 0 ? 'stop' : 'error',
            usage: finalUsage ?? {
              promptTokens,
              completionTokens: estimateTokens(completionText),
            },
          });
          controller.close();
        });
      },
    });
  }
}

export default class ClaudeCliProvider extends BaseProvider {
  name = 'ClaudeCLI';
  getApiKeyLink = 'https://code.claude.com/docs/en/setup';
  labelForGetApiKey = 'Install / login to Claude Code';
  icon = 'i-ph:terminal-window';

  config = {};

  staticModels: ModelInfo[] = [
    {
      name: 'claude-cli-default',
      label: 'Claude CLI Default',
      provider: this.name,
      maxTokenAllowed: 200000,
      maxCompletionTokens: 64000,
    },
    {
      name: 'claude-cli-sonnet',
      label: 'Claude CLI Sonnet',
      provider: this.name,
      maxTokenAllowed: 200000,
      maxCompletionTokens: 64000,
    },
    {
      name: 'claude-cli-opus',
      label: 'Claude CLI Opus',
      provider: this.name,
      maxTokenAllowed: 200000,
      maxCompletionTokens: 32000,
    },
  ];

  getModelInstance: (options: {
    model: string;
    serverEnv?: Env;
    apiKeys?: Record<string, string>;
    providerSettings?: Record<string, IProviderSetting>;
  }) => LanguageModelV1 = (options) => {
    const command =
      options.serverEnv?.CLAUDE_CLI_COMMAND ||
      process.env.CLAUDE_CLI_COMMAND ||
      options.providerSettings?.[this.name]?.baseUrl ||
      DEFAULT_CLAUDE_COMMAND;
    const extraArgs = splitShellLike(options.serverEnv?.CLAUDE_CLI_ARGS || process.env.CLAUDE_CLI_ARGS);
    const defaultModel = options.serverEnv?.CLAUDE_CLI_MODEL || process.env.CLAUDE_CLI_MODEL || DEFAULT_CLAUDE_MODEL;

    return new ClaudeCliLanguageModel(options.model, command, extraArgs, defaultModel);
  };
}
